import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Col, DatePicker, Input, Radio, RadioChangeEvent, Row, Select } from 'antd';
import locale from 'antd/es/date-picker/locale/ja_JP';
import { cssList } from 'src/utils/csslist';
import dayjs from 'dayjs';
import {
  SEARCH_EMPTY_SELECT,
  SectionDisplay, SectionDisplayKeys,
  SectionKeys,
  Sections,
  TimeSlot,
} from '../../../constant';
import { CustomDatePicker } from './custom_date_picker';

import styles from './index.module.scss';
import { useDivisionSelector } from '../../components/division_selector/interface';
import { TaskListParam } from './tasklist_store';
import { useIndicator } from '../../../hook/use_indicator';
import { SectionIcon } from '../../components/section_icon/section_icon';

/**
 * 検索エリアのプロパティ
 */
type Props = {
  defaultParam: TaskListParam,
  onSearch: (param: TaskListParam) => void,
  searching: boolean,
};

/**
 * 検索エリア
 * @param defaultParam
 * @param onSearch
 * @param searching
 * @constructor
 */
export const SearchPanel: React.FC<Props> = ({ defaultParam, onSearch, searching }) => {
  const [height, setHeight] = useState(0);
  const [originalHeight, setOriginalHeight] = useState(0);
  const [isSearchBox, setIsSearchBox] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const [param, setParam] = useState<TaskListParam>(defaultParam);
  const handleShowSearchButton = useCallback(() => {
    setIsSearchBox(!isSearchBox);
    setHeight(isSearchBox ? 40 : originalHeight);
  }, [isSearchBox]);
  const { DivisionSelector } = useDivisionSelector();

  useEffect(() => {
    if (ref.current) {
      setHeight(ref.current.scrollHeight);
      setOriginalHeight(ref.current.scrollHeight);
    }
  }, []);

  const style = useMemo(() => {
    if (height === 0) {
      return {};
    }
    return { height };
  }, [height]);

  const onChangeTime = (e: RadioChangeEvent) => {
    const { value } = e.target;
    if (value === 'morning' || value === 'afternoon' || value === 'night' || value === 'all_day') {
      setParam((p) => ({ ...p, timeType: 'timeSlot', span_of_time: value }));
    } else {
      setParam((p) => ({ ...p, timeType: value, span_of_time: TimeSlot.none }));
    }
  };

  const keyOptions = [
    ...Sections.filter((s) => s.key !== SectionKeys.bispot).map((section) => {
      return {
        value: section.key,
        label: (
            <span className={styles.sectionChk}>
            <SectionIcon sectionCode={section.key} size={(section.key === Sections[0].key ? 20 : 22)}/>
            <span className={styles.sectionChkText}>{section.value}</span>
          </span>
        ),
      };
    }),
  ];

  const timeRange: [ dayjs.Dayjs, dayjs.Dayjs ] | undefined = [
    param.start_time === undefined ? undefined : dayjs(param.start_time, 'HH:mm:ss'),
    param.end_time === undefined ? undefined : dayjs(param.end_time, 'HH:mm:ss'),
  ];

  const update = (param: Partial<TaskListParam>) => {
    setParam((p) => ({ ...p, ...param }));
  };

  const handleChangeTime = (time: [ dayjs.Dayjs, dayjs.Dayjs ]) => {
    if (time === null) {
      setParam((p) => ({ ...p, start_time: undefined, end_time: undefined }));
    } else {
      setParam((p) => ({
        ...p,
        start_time: time[0].format('HH:mm:ss'),
        end_time: time[1].format('HH:mm:ss'),
      }));
    }
  };

  const handleApplyDate = (type: 'none' | 'range' | 'day', from?: string, to?: string) => {
    setParam((p) => ({ ...p, applyDateType: type, apply_from: from, apply_to: to }));
  };
  const handleWorkDate = (type: 'none' | 'range' | 'day', from?: string, to?: string) => {
    setParam((p) => ({ ...p, workDateType: type, work_from: from, work_to: to }));
  };

  return (
      <div
          ref={ref}
          style={style}
          className={cssList(styles.section, styles.relative, styles.searchBox, {
            [styles.hide]: !isSearchBox,
          })}
      >
      <span className={styles.menuShowBtn}>
        <Button
            type={'dashed'}
            size={'small'}
            onClick={handleShowSearchButton}
            style={{ color: 'gray' }}
        >
          {isSearchBox ?? true ? '検索条件 非表示' : '検索条件 表示'}
        </Button>
      </span>
        <div className={styles.searchBox + ' ' + (isSearchBox ? '' : styles.hide)}>
          <Row className={styles.row}>
            <div className={styles.formLabel}>
              検索区分
            </div>
            <Col>
              <Checkbox.Group
                  options={keyOptions}
                  onChange={(values) => update({ sections: values })} defaultValue={param.sections ?? []}
              />
            </Col>
          </Row>
          <DivisionSelector
              defaultDivisionId={param.division}
              defaultFloorId={param.floor ?? 0}
              defaultBlockId={param.block ?? 0}
              onChange={({ divisionId, floorId, blockId }) => {
                update({ division: divisionId, floor: floorId, block: blockId });
              }}
          />
          <CustomDatePicker title={'統合管理受付日'} onChange={handleApplyDate} type={param.applyDateType} from={param.apply_from} to={param.apply_to}/>
          <CustomDatePicker title={'作業日'} onChange={handleWorkDate} type={param.workDateType} from={param.work_from} to={param.work_to}/>
          <Row className={styles.row}>
            <div className={styles.formLabel}>
              時間帯
            </div>
            <Col>
              <Radio.Group defaultValue={param.timeType} onChange={onChangeTime}>
                <Radio value={'none'}>指定なし</Radio>
                <Radio value={'range'}>
                  時間指定&nbsp;&nbsp;&nbsp;&nbsp;
                  <DatePicker.RangePicker defaultValue={timeRange} onChange={handleChangeTime} picker="time" format='HH:mm' locale={locale} allowEmpty={param.timeType !== 'range'} disabled={param.timeType !== 'range'}/>
                </Radio>
                <Radio value={'morning'}>午前</Radio>
                <Radio value={'afternoon'}>午後</Radio>
                <Radio value={'night'}>夜間</Radio>
                <Radio value={'all_day'}>全日</Radio>
              </Radio.Group>
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={styles.formLabel}>
              項目
            </div>
            <Col>
              <Select
                  style={{ width: 120 }}
                  value={param.textType}
                  onChange={(value) => { update({ textType: value }); }}
                  options={[
                    {
                      value: '0',
                      label: SEARCH_EMPTY_SELECT,
                    },
                    ...[
                      { key: 'target', value: '作業場所' },
                      { key: 'work', value: '作業内容' },
                    ].map((textType) => ({ value: textType.key, label: textType.value })),
                  ]}
              />
              &nbsp;&nbsp;に&nbsp;&nbsp;
              <Input value={param.content} onChange={(e) => { update({ content: e.target.value }); }} style={{ width: 250 }}/>
              &nbsp;&nbsp;を含む
            </Col>
          </Row>
          <Row className={styles.row}>
            <div className={styles.formLabel}>
              件数
            </div>
            <Col>
              <Select
                  style={{ width: 120 }}
                  value={param.count}
                  onChange={(value) => { update({ count: value }); }}
                  options={[
                    ...[25, 50, 100].map((count) => ({ value: count, label: `${count}件` })),
                  ]}
              />
            </Col>
          </Row>
          <Row style={{ marginTop: '20px' }}>
            <Col>
              <Button type="primary" loading={searching} onClick={() => {
                onSearch(param);
              }}>検索</Button>
            </Col>
          </Row>
        </div>
      </div>
  );
};
